import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "i18n-fe";
import { useActiveOrg } from "common-fe";
import { useStationName } from "../hooks/useStationIdentity";
import { syncEngine } from "../local/sync";
import { countUnsentSheets } from "../local/clearMachine";
import { stationIdSuffix } from "score-common";
import { getPresenceInstanceId } from "../util/presenceInstanceId";
import RebuildLocalDataDialog from "./RebuildLocalDataDialog";
import ResetTabletDialog from "./ResetTabletDialog";

// #3568 -- the ONE unlocked screen on a scribe tablet. The padlock and the first
// setup both lead here; the show PIN (or no session yet) is the only gate, and
// nothing on it asks for an admin sign-in. Title, then the station and tablet id;
// a gear at the top right opens the two tablet tools (Rebuild local data, Reset
// tablet); the page body is the Select Show section (the caller's children); ONE
// exit button at the bottom.
export default function AdminOptions({ children, onBack, backLabel, backDisabled = false, online = true }) {
  const { t } = useTranslation("score");
  const { activeOrgId } = useActiveOrg();
  const stationName = useStationName(activeOrgId);
  const [anchor, setAnchor] = useState(null);
  const [dialog, setDialog] = useState(null); // "rebuild" | "reset" | null
  const [unsent, setUnsent] = useState(0);
  const selectShowRef = useRef(null);

  const refresh = useCallback(() => {
    countUnsentSheets().then(setUnsent, () => {});
  }, []);
  useEffect(() => {
    refresh();
    return syncEngine.subscribe(() => refresh());
  }, [refresh]);

  const tablet = stationIdSuffix(getPresenceInstanceId()) || getPresenceInstanceId().slice(-4);
  const menuOpen = Boolean(anchor);
  const closeMenu = () => setAnchor(null);
  const openDialog = (which) => {
    refresh();
    closeMenu();
    setDialog(which);
  };

  const resetDesc = !unsent
    ? t("adminOptions.resetDescNone")
    : online
      ? t("adminOptions.resetDescOnline", { count: unsent })
      : t("adminOptions.resetDescOffline", { count: unsent });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        pt: "72px",
        bgcolor: "background.default",
      }}
    >
      <Card elevation={4} sx={{ width: "100%", maxWidth: 560, borderRadius: 3 }} data-testid="admin-options">
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Typography variant="h4" component="h1" fontWeight={500}>
                  {t("adminOptions.title")}
                </Typography>
                <Typography color="text.secondary" data-testid="admin-options-station">
                  {t("adminOptions.stationLine", {
                    station: stationName ?? t("adminOptions.unnamedStation"),
                    tablet,
                  })}
                </Typography>
              </Box>
              <IconButton
                aria-label={t("adminOptions.gearAria")}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={(e) => setAnchor(e.currentTarget)}
                data-testid="admin-options-gear"
                sx={{ border: 1, borderColor: "divider", width: 48, height: 48 }}
              >
                <SettingsIcon />
              </IconButton>
              <Menu
                anchorEl={anchor}
                open={menuOpen}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ list: { "aria-label": t("adminOptions.gearAria") } }}
              >
                <MenuItem onClick={() => openDialog("rebuild")} data-testid="admin-options-rebuild" sx={{ whiteSpace: "normal", maxWidth: 320 }}>
                  <Stack>
                    <Typography fontWeight={500}>{t("adminOptions.rebuildTitle")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("adminOptions.rebuildDesc")}
                    </Typography>
                  </Stack>
                </MenuItem>
                <MenuItem onClick={() => openDialog("reset")} data-testid="admin-options-reset" sx={{ whiteSpace: "normal", maxWidth: 320 }}>
                  <Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={500} color="error">
                        {t("adminOptions.resetTitle")}
                      </Typography>
                      {unsent > 0 && (
                        <Chip size="small" color="error" variant="outlined" label={t("adminOptions.unsentChip", { count: unsent })} />
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" data-testid="admin-options-reset-desc">
                      {resetDesc}
                    </Typography>
                  </Stack>
                </MenuItem>
              </Menu>
            </Stack>
            <Divider />
            <Box ref={selectShowRef} data-testid="admin-options-select-show">
              {children}
            </Box>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="large"
                onClick={onBack}
                disabled={backDisabled}
                data-testid="admin-options-back"
                sx={{ minWidth: 160, py: 1.25, fontWeight: 600 }}
              >
                {backLabel}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <RebuildLocalDataDialog open={dialog === "rebuild"} onClose={() => setDialog(null)} />
      <ResetTabletDialog
        open={dialog === "reset"}
        onClose={() => setDialog(null)}
        onSelectShow={() => selectShowRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" })}
        unsent={unsent}
        online={online}
      />
    </Box>
  );
}
